import { useEffect, useMemo, useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { fmtDate } from '@/lib/format';
import { CheckCircle2, ChevronLeft, LinkIcon, Search, ArrowRight, ShieldCheck } from 'lucide-react';

/**
 * Iter39 — Two-step "Attach Work Order" flow for Master Project Data.
 *
 * Step 1:  Searchable selector of available WOs (`GET /work_orders/available`).
 * Step 2:  Review — shows project + WO summary + `will_fill` fields (what would
 *          be copied — ONLY fields that are currently blank on the MP) +
 *          `protected_fields` (values that will NOT be touched).
 * Confirm: POST /master-projects/:id/attach-work-order — writes both sides.
 *
 * Rules (from Owner brief 2026-02):
 *  - never overwrites user-entered project fields; only fills blanks
 *  - WOs already linked to another MP are filtered out server-side
 *  - MPs already linked show a "Change / Detach" flow instead of this dialog
 */
export function AttachWorkOrderDialog({ project, open, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [q, setQ] = useState('');
  const [wos, setWos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setStep(1); setQ(''); setWos([]); setSelected(null);
    setPreview(null); setError(''); setBusy(false); setLoading(false);
  };
  const handleClose = () => { reset(); onClose?.(); };

  // Load available WOs on open + on query change
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/work_orders/available', { params: q ? { q } : {} });
        if (alive) setWos(r.data || []);
      } catch (e) { toast.error(parseErr(e)); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [open, q]);

  const proceedToReview = async (wo) => {
    setSelected(wo);
    setError('');
    setBusy(true);
    try {
      const r = await api.post(`/master-projects/${project.id}/attach-work-order/preview`,
                                { work_order_id: wo.id });
      setPreview(r.data);
      setStep(2);
    } catch (e) { setError(parseErr(e)); }
    finally { setBusy(false); }
  };

  const confirmAttach = async () => {
    if (!selected) return;
    setError(''); setBusy(true);
    try {
      const r = await api.post(`/master-projects/${project.id}/attach-work-order`,
                                { work_order_id: selected.id });
      toast.success(`"${project.project_name}" is now linked to ${selected.wo_number}`);
      onDone?.(r.data);
      reset();
    } catch (e) { setError(parseErr(e)); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => wos, [wos]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-[620px] max-h-[85vh] overflow-y-auto"
                     data-testid={`attach-wo-dialog-${project?.id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkIcon size={16} />
            {step === 1 ? 'Attach a Work Order — Step 1: Select' : 'Attach a Work Order — Step 2: Confirm'}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? 'Pick an existing Work Order to link to this project. Only unlinked, active WOs are shown.'
              : 'Review the assignment. Your existing project details are protected — only blank fields will be filled from the WO.'}
          </DialogDescription>
        </DialogHeader>

        {/* Project context strip */}
        <div className="rounded-md p-2.5 text-[12.5px] border flex items-center gap-2 mt-1"
             style={{ borderColor: 'hsl(48 90% 75%)', background: 'hsl(48 96% 96%)' }}>
          <span className="text-[9.5px] font-semibold px-1.5 py-[1px] rounded-full uppercase tracking-wide"
                style={{ background: 'hsl(48 90% 55%)', color: '#3a2900' }}>
            {project?.code || 'Unlinked'}
          </span>
          <span className="truncate flex-1"><strong>{project?.project_name}</strong> · {project?.client}</span>
          <ArrowRight size={12} className="text-[hsl(var(--foreground-subtle))]" />
          <span className="text-[hsl(var(--success))] font-medium">Billable</span>
        </div>

        {/* ============================ STEP 1 ============================ */}
        {step === 1 && (
          <div className="mt-3" data-testid="attach-wo-step1">
            <div className="relative">
              <Search size={13} className="absolute left-2 top-2.5 text-[hsl(var(--foreground-subtle))]" />
              <Input value={q} onChange={e => setQ(e.target.value)}
                     placeholder="Search by WO number or client…"
                     className="pl-7"
                     data-testid="attach-wo-search" />
            </div>
            <div className="mt-2 border rounded-md divide-y max-h-[360px] overflow-y-auto"
                 style={{ borderColor: 'hsl(var(--border))' }}>
              {loading && <div className="p-4 text-[12.5px] text-[hsl(var(--foreground-muted))]">Loading…</div>}
              {!loading && filtered.length === 0 && (
                <div className="p-4 text-[12.5px] text-[hsl(var(--foreground-muted))]"
                     data-testid="attach-wo-empty">
                  No unlinked Work Orders match. Upload a new one from Work Orders, then come back here.
                </div>
              )}
              {!loading && filtered.map(w => (
                <button key={w.id}
                        onClick={() => proceedToReview(w)}
                        disabled={busy}
                        data-testid={`attach-wo-row-${w.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-[hsl(var(--surface-1))] disabled:opacity-50">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[13.5px] font-medium truncate">{w.wo_number || <em>(no WO number)</em>}</div>
                      <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))] truncate">
                        {w.client_name || '—'}{w.wo_date ? ` · ${fmtDate(w.wo_date)}` : ''}
                      </div>
                    </div>
                    <span className="chip">{w.status}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ============================ STEP 2 ============================ */}
        {step === 2 && preview && (
          <div className="mt-3 space-y-3" data-testid="attach-wo-step2">
            <div className="grid grid-cols-2 gap-3 text-[12.5px]">
              <div className="border rounded-md p-3" style={{ borderColor: 'hsl(var(--border))' }}>
                <div className="text-[11px] uppercase tracking-wide text-[hsl(var(--foreground-subtle))]">Project</div>
                <div className="mt-1 font-medium">{preview.project?.project_name}</div>
                <div>{preview.project?.code} · {preview.project?.client}</div>
                <div className="text-[hsl(var(--foreground-muted))]">Stage: {preview.project?.current_stage}</div>
              </div>
              <div className="border rounded-md p-3" style={{ borderColor: 'hsl(var(--border))' }}>
                <div className="text-[11px] uppercase tracking-wide text-[hsl(var(--foreground-subtle))]">Work Order</div>
                <div className="mt-1 font-medium">{preview.work_order?.wo_number || '—'}</div>
                <div>{preview.work_order?.wo_date ? `Dated ${fmtDate(preview.work_order.wo_date)}` : 'No date'}</div>
                {preview.work_order?.total_amount != null && (
                  <div className="text-[hsl(var(--foreground-muted))]">
                    Fee: <b>{preview.work_order.total_amount.toLocaleString()}</b>
                  </div>
                )}
                {preview.work_order?.file_url && (
                  <a href={preview.work_order.file_url} target="_blank" rel="noreferrer"
                     className="text-[hsl(var(--primary))] underline text-[11.5px]">Open WO file</a>
                )}
              </div>
            </div>

            {/* will_fill */}
            {Object.keys(preview.will_fill || {}).length > 0 && (
              <div className="border rounded-md p-3 bg-[hsl(var(--surface-1))]"
                   style={{ borderColor: 'hsl(var(--border))' }}
                   data-testid="attach-wo-will-fill">
                <div className="text-[11.5px] font-medium text-[hsl(var(--foreground-muted))] flex items-center gap-1 mb-1">
                  <CheckCircle2 size={12} className="text-[hsl(var(--success))]" />
                  Will fill (only blank fields — nothing you typed will be overwritten):
                </div>
                <ul className="text-[12.5px] space-y-0.5">
                  {Object.entries(preview.will_fill).map(([k, v]) => (
                    <li key={k}>· <b>{k}</b> → {String(v)}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* protected */}
            {(preview.protected_fields || []).length > 0 && (
              <div className="border rounded-md p-3"
                   style={{ borderColor: 'hsl(var(--border))' }}
                   data-testid="attach-wo-protected">
                <div className="text-[11.5px] font-medium text-[hsl(var(--foreground-muted))] flex items-center gap-1 mb-1">
                  <ShieldCheck size={12} className="text-[hsl(var(--primary))]" />
                  Protected — keeps your values as-is:
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {preview.protected_fields.map(f => (
                    <span key={f} className="chip text-[10.5px]">{f}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Cascade note */}
            <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))]">
              Stage sign-offs preserved · Milestones: {preview.cascade?.milestones}
            </div>
          </div>
        )}

        {error && (
          <div className="text-[12.5px] text-[hsl(var(--danger))] p-2 rounded border mt-2"
               style={{ borderColor: 'hsl(var(--danger))', background: 'hsl(0 90% 97%)' }}
               role="alert" data-testid="attach-wo-error">
            {error}
          </div>
        )}

        <DialogFooter>
          {step === 2 && (
            <Button variant="ghost" onClick={() => { setStep(1); setPreview(null); }}
                    data-testid="attach-wo-back"><ChevronLeft size={13} className="mr-1" />Back</Button>
          )}
          <Button variant="ghost" onClick={handleClose} disabled={busy}>Cancel</Button>
          {step === 2 && (
            <Button onClick={confirmAttach} disabled={busy || !selected}
                    data-testid="attach-wo-confirm">
              {busy ? 'Attaching…' : 'Confirm Assignment'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
